// Apify node side-panel editor. Three parts, top to bottom:
//   1. API key  — the per-node Apify token (needed to RUN an actor).
//   2. Actor    — search the public Apify Store and pick an actor.
//   3. Input    — a form generated from the picked actor's input schema.
//
// It writes plain hyperparameter values via onParamChange (same contract as
// BrowserActionEditor): apiKey, actorId, actorTitle, inputSchema (cached JSON
// string) and input (assembled actor-input JSON string). The handler in
// api/src/nodes/apify/handler.js reads apiKey + actorId + input at run time.

const React = require('react');
const { useEffect, useRef, useState } = React;
const { createElement: h } = React;
const apiClient = require('../lib/apiClient');
const VariableAutocompleteInput = require('./VariableAutocompleteInput');
const { getFields, widgetFor, seedInput, htmlToPlainText } = require('../lib/apifyInputSchema');
require('./ApifyEditor.css');

function parseJson(raw, fallback) {
  try {
    const v = JSON.parse(String(raw == null ? '' : raw));
    return v && typeof v === 'object' ? v : fallback;
  } catch (_) {
    return fallback;
  }
}

// Star rating + review count and a pricing chip for one actor. Rendered in
// both the search result rows and the selected-actor chip. Any missing piece
// is simply omitted, so it collapses to nothing when there's no data.
function ActorMeta(props) {
  const rating = props.rating;
  const reviewCount = Number(props.reviewCount) || 0;
  const pricing = props.pricing;
  const freeTrial = props.freeTrial;
  if ((rating == null || rating === '') && !pricing && !freeTrial) return null;
  return h('div', { className: 'wf-apify-meta' },
    (rating != null && rating !== '')
      ? h('span', { className: 'wf-apify-rating' },
          h('span', { className: 'wf-apify-star' }, '★'),
          String(rating),
          reviewCount ? h('span', { className: 'wf-apify-reviews' }, ' (' + reviewCount.toLocaleString() + ')') : null
        )
      : null,
    pricing ? h('span', { className: 'wf-apify-price' }, pricing) : null,
    freeTrial ? h('span', { className: 'wf-apify-trial' }, freeTrial) : null
  );
}

// A numbered section header + indented body — mirrors BrowserActionEditor so
// both browser-style nodes read the same way.
function Section(props) {
  return h('div', { className: 'wf-apify-section' },
    h('div', { className: 'wf-apify-section-head' },
      h('span', { className: 'wf-apify-badge' }, props.n),
      h('span', { className: 'wf-apify-section-label' }, props.label),
      props.required ? h('span', { className: 'wf-apify-required' }, 'Required') : null
    ),
    h('div', { className: 'wf-apify-section-body' }, props.children)
  );
}

// Return v when it's a plain (non-array) object, else null.
function plainObject(v) {
  return (v && typeof v === 'object' && !Array.isArray(v)) ? v : null;
}

// A "blank" copy of a value, preserving structure but clearing primitives —
// used to spawn a fresh array item shaped like the existing ones (e.g. adding
// a new { url: '' } to a startUrls list).
function emptyLike(v) {
  if (Array.isArray(v)) return [];
  if (v && typeof v === 'object') {
    const o = {};
    Object.keys(v).forEach(function (k) { o[k] = emptyLike(v[k]); });
    return o;
  }
  if (typeof v === 'number') return '';
  if (typeof v === 'boolean') return false;
  return '';
}

function defaultForType(type) {
  if (type === 'object') return {};
  if (type === 'array') return [];
  if (type === 'boolean') return false;
  return '';
}

// What a brand-new item should look like for an (empty) array field: shape it
// from the example (prefill/default) items when present, else from the schema's
// declared item type, else a plain string.
function sampleEmptyItem(prop) {
  const arr = Array.isArray(prop.prefill) ? prop.prefill
    : (Array.isArray(prop.default) ? prop.default : null);
  if (arr && arr.length) return emptyLike(arr[0]);
  if (prop.items && prop.items.type) return defaultForType(prop.items.type);
  return '';
}

// One primitive control (text / number / enum-select). Booleans are handled by
// the callers so they can attach the right label. `meta` may carry enum info.
function PrimitiveControl(props) {
  const value = props.value;
  const meta = props.meta || null;
  if (typeof value === 'number') {
    return h('input', {
      className: 'wf-params-input', type: 'number',
      value: value,
      onChange: function (e) { props.onChange(e.target.value === '' ? '' : Number(e.target.value)); }
    });
  }
  if (meta && Array.isArray(meta.enum) && meta.enum.length) {
    const titles = meta.enumTitles || [];
    return h('select', {
      className: 'wf-params-input', value: value == null ? '' : String(value),
      onChange: function (e) { props.onChange(e.target.value); }
    },
      meta.enum.map(function (opt, i) { return h('option', { key: String(opt), value: String(opt) }, titles[i] || String(opt)); })
    );
  }
  return h('input', {
    className: 'wf-params-input', type: 'text',
    value: value == null ? '' : String(value),
    onChange: function (e) { props.onChange(e.target.value); }
  });
}

// Recursive, non-technical editor for a JSON value:
//   array   → an add/remove list, one editor per item
//   object  → a labelled tree (key + nested editor), primitives inline
//   boolean → checkbox · number/string/enum → input/select
// `itemMeta` carries an array's item enum so string items render as dropdowns.
// `emptyItem` is the template used when adding to an empty array.
function TreeValueEditor(props) {
  const value = props.value;
  const onChange = props.onChange;

  if (Array.isArray(value)) {
    const hasEnum = props.itemMeta && Array.isArray(props.itemMeta.enum) && props.itemMeta.enum.length;
    // Enum items always spawn a valid choice; otherwise clone the last item's
    // shape (or the field's example item when the list is empty).
    const addTemplate = hasEnum ? props.itemMeta.enum[0]
      : (value.length ? emptyLike(value[value.length - 1])
        : (props.emptyItem !== undefined ? props.emptyItem : ''));
    return h('div', { className: 'wf-apify-tree' },
      value.length === 0 ? h('div', { className: 'wf-apify-tree-empty' }, 'No items yet.') : null,
      value.map(function (item, i) {
        return h('div', { key: i, className: 'wf-apify-tree-row' },
          h('div', { className: 'wf-apify-tree-body' },
            h(TreeValueEditor, {
              value: item,
              itemMeta: props.itemMeta,
              onChange: function (nv) { const c = value.slice(); c[i] = nv; onChange(c); }
            })
          ),
          h('button', {
            type: 'button', className: 'wf-apify-tree-remove', title: 'Remove item',
            onClick: function () { const c = value.slice(); c.splice(i, 1); onChange(c); }
          }, '✕')
        );
      }),
      h('button', {
        type: 'button', className: 'wf-apify-tree-add',
        onClick: function () { onChange(value.concat([addTemplate])); }
      }, '+ Add item')
    );
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    return h('div', { className: 'wf-apify-tree' },
      keys.length === 0 ? h('div', { className: 'wf-apify-tree-empty' }, 'No fields.') : null,
      keys.map(function (k) {
        const cv = value[k];
        function setK(nv) { const c = Object.assign({}, value); c[k] = nv; onChange(c); }
        // Boolean → inline checkbox using the key as its label.
        if (typeof cv === 'boolean') {
          return h('label', { key: k, className: 'wf-apify-tree-check' },
            h('input', { type: 'checkbox', checked: cv, onChange: function (e) { setK(e.target.checked); } }),
            h('span', { className: 'wf-apify-tree-keyinline' }, k)
          );
        }
        // Nested object/array → key label above, recurse below.
        if (Array.isArray(cv) || (cv && typeof cv === 'object')) {
          return h('div', { key: k, className: 'wf-apify-tree-kv' },
            h('div', { className: 'wf-apify-tree-key' }, k),
            h(TreeValueEditor, { value: cv, onChange: setK })
          );
        }
        // Primitive → key + control on one row.
        return h('div', { key: k, className: 'wf-apify-tree-inline' },
          h('div', { className: 'wf-apify-tree-key' }, k),
          h(PrimitiveControl, { value: cv, onChange: setK })
        );
      })
    );
  }

  // Standalone primitive (e.g. an array of strings).
  if (typeof value === 'boolean') {
    return h('label', { className: 'wf-apify-tree-check' },
      h('input', { type: 'checkbox', checked: value, onChange: function (e) { onChange(e.target.checked); } }),
      h('span', null, 'Enabled')
    );
  }
  return h(PrimitiveControl, { value: value, onChange: onChange, meta: props.itemMeta });
}

// One input-schema field → one widget. Complex types (arrays/objects) fall
// back to a raw-JSON textarea. `value` is the current value from the input
// object; `onValue(next)` writes it back (pass undefined to drop the key).
function SchemaField(props) {
  const prop = props.prop || {};
  const widget = widgetFor(prop);
  const label = htmlToPlainText(prop.title || props.fieldKey);
  // Apify descriptions are HTML — always plain-text them before rendering.
  const desc = htmlToPlainText(prop.description || '');
  const variableNodes = props.variableNodes || [];

  // Raw-JSON widget keeps its own draft text so typing invalid JSON mid-edit
  // doesn't wipe the committed value; it commits only when the text parses.
  const [jsonText, setJsonText] = useState(function () {
    return props.value === undefined ? '' : JSON.stringify(props.value, null, 2);
  });
  const [jsonErr, setJsonErr] = useState('');
  // Long help texts are clamped to a few lines with a Show more/less toggle.
  const [descOpen, setDescOpen] = useState(false);
  const descIsLong = desc.length > 160;
  // Object fields render as a key/value form by default; power users can flip
  // to raw JSON (and it auto-flips there when the shape is too complex).
  const [jsonMode, setJsonMode] = useState(false);

  // Raw-JSON textarea, shared by array fields and the object "Edit as JSON"
  // escape hatch. Commits only when the text parses; empty clears the field.
  function jsonTextarea() {
    return h('div', null,
      h('textarea', {
        className: 'wf-params-input wf-apify-json',
        rows: 4,
        value: jsonText,
        placeholder: '[] or { }',
        onChange: function (e) {
          const text = e.target.value;
          setJsonText(text);
          if (text.trim() === '') { setJsonErr(''); props.onValue(undefined); return; }
          try {
            const parsed = JSON.parse(text);
            setJsonErr('');
            props.onValue(parsed);
          } catch (err) {
            setJsonErr('Not valid JSON yet — keep typing.');
          }
        }
      }),
      jsonErr ? h('div', { className: 'wf-apify-field-err' }, jsonErr) : null
    );
  }

  let control;
  if (widget === 'checkbox') {
    control = h('label', { className: 'wf-apify-check' },
      h('input', {
        type: 'checkbox',
        checked: props.value === true,
        onChange: function (e) { props.onValue(e.target.checked); }
      }),
      h('span', null, desc || 'Enabled')
    );
  } else if (widget === 'number') {
    control = h('input', {
      className: 'wf-params-input',
      type: 'number',
      value: props.value === undefined || props.value === null ? '' : props.value,
      placeholder: prop.prefill !== undefined ? String(prop.prefill) : '',
      onChange: function (e) {
        const raw = e.target.value;
        props.onValue(raw === '' ? undefined : Number(raw));
      }
    });
  } else if (widget === 'select') {
    const options = Array.isArray(prop.enum) ? prop.enum : [];
    const titles = Array.isArray(prop.enumTitles) ? prop.enumTitles : [];
    control = h('select', {
      className: 'wf-params-input',
      value: props.value === undefined ? '' : String(props.value),
      onChange: function (e) { props.onValue(e.target.value === '' ? undefined : e.target.value); }
    },
      h('option', { value: '' }, '— select —'),
      options.map(function (opt, i) {
        return h('option', { key: String(opt), value: String(opt) }, titles[i] || String(opt));
      })
    );
  } else if (widget === 'textarea') {
    control = h(VariableAutocompleteInput, {
      value: props.value === undefined ? '' : String(props.value),
      multiline: true,
      rows: 4,
      placeholder: prop.prefill !== undefined ? String(prop.prefill) : 'Text · or @variable',
      variableNodes: variableNodes,
      onChange: function (next) { props.onValue(next === '' ? undefined : next); }
    });
  } else if (widget === 'tree') {
    // Friendly recursive editor for arrays (add/remove lists) and objects
    // (labelled tree). Falls back to the value's example shape when unset, and
    // offers a raw-JSON escape hatch for power users.
    const isArrayField = String(prop.type) === 'array';
    let cur = props.value;
    if (cur === undefined) {
      cur = prop.prefill !== undefined ? prop.prefill
        : (prop.default !== undefined ? prop.default : (isArrayField ? [] : {}));
    }
    if (isArrayField && !Array.isArray(cur)) cur = [];
    if (!isArrayField && !plainObject(cur)) cur = {};
    const itemMeta = (isArrayField && prop.items)
      ? { enum: prop.items.enum, enumTitles: prop.items.enumTitles } : null;
    control = jsonMode
      ? h('div', null,
          jsonTextarea(),
          h('button', {
            type: 'button', className: 'wf-apify-desc-toggle',
            onClick: function () { setJsonMode(false); }
          }, 'Back to fields')
        )
      : h('div', null,
          h(TreeValueEditor, {
            value: cur,
            itemMeta: itemMeta,
            emptyItem: isArrayField ? sampleEmptyItem(prop) : undefined,
            onChange: function (nv) { props.onValue(nv); }
          }),
          h('button', {
            type: 'button', className: 'wf-apify-desc-toggle',
            onClick: function () { setJsonText(JSON.stringify(cur, null, 2)); setJsonMode(true); }
          }, 'Edit as JSON')
        );
  } else if (widget === 'json') {
    control = jsonTextarea();
  } else {
    // Plain text (supports @variable injection).
    control = h(VariableAutocompleteInput, {
      value: props.value === undefined ? '' : String(props.value),
      placeholder: prop.prefill !== undefined ? String(prop.prefill) : 'Value · or @variable',
      variableNodes: variableNodes,
      onChange: function (next) { props.onValue(next === '' ? undefined : next); }
    });
  }

  return h('div', { className: 'wf-apify-field' },
    h('div', { className: 'wf-apify-field-label' },
      label,
      props.required ? h('span', { className: 'wf-apify-field-req' }, ' *') : null
    ),
    // Boolean already shows its description inline next to the checkbox.
    desc && widget !== 'checkbox'
      ? h('div', null,
          h('div', {
            className: 'wf-apify-field-desc' + (descIsLong && !descOpen ? ' wf-apify-field-desc-clamp' : ''),
            title: descIsLong ? desc : undefined
          }, desc),
          descIsLong ? h('button', {
            type: 'button',
            className: 'wf-apify-desc-toggle',
            onClick: function () { setDescOpen(function (v) { return !v; }); }
          }, descOpen ? 'Show less' : 'Show more') : null
        )
      : null,
    control
  );
}

function ApifyEditor(props) {
  const paramValues = props.paramValues || {};
  const onParamChange = props.onParamChange || function () {};
  const variableNodes = props.variableNodes || [];

  const apiKey = String(paramValues.apiKey || '');
  const actorId = String(paramValues.actorId || '');
  const actorTitle = String(paramValues.actorTitle || '');
  const actorMeta = parseJson(paramValues.actorMeta, null);
  const schema = parseJson(paramValues.inputSchema, null);
  const input = parseJson(paramValues.input, {});

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState('');
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [schemaErr, setSchemaErr] = useState('');
  const [showKey, setShowKey] = useState(false);
  // Live permission info for the selected actor (also mirrored into actorMeta
  // so the approval notice survives reopening the node).
  const [permission, setPermission] = useState(null);
  const debounceRef = useRef(null);

  // Debounced Store search (public — no token needed). Only fires while the
  // user is actively typing a query and no actor is selected yet.
  useEffect(function () {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(function () {
      apiClient.request('/apify/actors/search?q=' + encodeURIComponent(q))
        .then(function (res) {
          setResults((res && Array.isArray(res.actors)) ? res.actors : []);
          setSearching(false);
          setSearchErr('');
        })
        .catch(function (err) {
          setSearching(false);
          setSearchErr(err && err.message ? err.message : 'Actor search failed.');
        });
    }, 350);
    return function () { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  function setInputKey(key, value) {
    const next = Object.assign({}, input);
    if (value === undefined) delete next[key]; else next[key] = value;
    onParamChange('input', JSON.stringify(next));
  }

  // Fetch the picked actor's input schema + permission info, seed the input,
  // and persist a compact actorMeta (rating/pricing/trial + whether the actor
  // needs a one-time permission approval to run).
  function loadActor(a, token) {
    setLoadingSchema(true);
    setSchemaErr('');
    setPermission(null);
    apiClient.request('/apify/actors/' + encodeURIComponent(a.id) + '/input-schema?token=' + encodeURIComponent(token || ''))
      .then(function (res) {
        const sch = res && res.schema ? res.schema : null;
        const perm = (res && res.permission) || null;
        setPermission(perm);
        onParamChange('inputSchema', sch ? JSON.stringify(sch) : '');
        onParamChange('input', JSON.stringify(sch ? seedInput(sch) : {}));
        onParamChange('actorMeta', JSON.stringify({
          rating: a.rating, reviewCount: a.reviewCount, pricing: a.pricing, freeTrial: a.freeTrial,
          requiresApproval: !!(perm && perm.requiresApproval)
        }));
        setLoadingSchema(false);
      })
      .catch(function (err) {
        setLoadingSchema(false);
        setSchemaErr(err && err.message ? err.message : 'Could not load the actor input fields.');
      });
  }

  function selectActor(a) {
    onParamChange('actorId', a.id);
    onParamChange('actorTitle', (a.title || a.name) + ' — ' + a.username + '/' + a.name);
    setQuery('');
    setResults([]);
    loadActor(a, apiKey);
  }

  function clearActor() {
    onParamChange('actorId', '');
    onParamChange('actorTitle', '');
    onParamChange('actorMeta', '');
    onParamChange('inputSchema', '');
    onParamChange('input', '{}');
    setSchemaErr('');
    setPermission(null);
  }

  let n = 0;
  const next = function () { n += 1; return n; };

  const fields = schema ? getFields(schema) : [];

  // Whether the picked actor needs a one-time permission approval before it can
  // run. The approve URL is deterministic from the actor id, so we can offer it
  // up front (from live permission info, or the value persisted in actorMeta).
  const needsApproval = !!((permission && permission.requiresApproval) || (actorMeta && actorMeta.requiresApproval));
  const approveUrl = actorId
    ? 'https://console.apify.com/actors/' + encodeURIComponent(actorId) + '?approvePermissions=true'
    : '';

  return h('div', { className: 'wf-apify-form' },
    // 1 — API key
    h(Section, { n: next(), label: 'Apify API key', required: true },
      h('div', { className: 'wf-apify-key-row' },
        h('input', {
          className: 'wf-params-input',
          type: showKey ? 'text' : 'password',
          value: apiKey,
          placeholder: 'apify_api_…',
          autoComplete: 'off',
          spellCheck: false,
          onChange: function (e) { onParamChange('apiKey', e.target.value); }
        }),
        h('button', {
          type: 'button',
          className: 'wf-apify-key-toggle',
          onClick: function () { setShowKey(function (v) { return !v; }); }
        }, showKey ? 'Hide' : 'Show')
      ),
      h('div', { className: 'wf-apify-hint' },
        'Find it in Apify Console → Settings → API & Integrations. Used to run the actor.')
    ),

    // 2 — Actor picker
    h(Section, { n: next(), label: 'Actor', required: true },
      actorId
        ? h(React.Fragment, null,
            h('div', { className: 'wf-apify-actor-selected' },
              h('div', { className: 'wf-apify-actor-selected-main' },
                h('div', { className: 'wf-apify-actor-name' }, actorTitle || actorId),
                actorMeta ? h(ActorMeta, {
                  rating: actorMeta.rating,
                  reviewCount: actorMeta.reviewCount,
                  pricing: actorMeta.pricing,
                  freeTrial: actorMeta.freeTrial
                }) : null
              ),
              h('button', {
                type: 'button',
                className: 'wf-apify-text-btn',
                onClick: clearActor
              }, 'Change')
            ),
            // This actor requests full account access — Apify blocks the run
            // until the user approves it once in the Console. Offer the approve
            // link up front so they can do it before running.
            needsApproval ? h('div', { className: 'wf-apify-notice' },
              h('span', { className: 'wf-apify-notice-icon' }, '⚠'),
              h('span', null,
                'This actor needs a one-time permission approval before it can run. ',
                h('a', {
                  className: 'wf-apify-approve-link',
                  href: approveUrl,
                  target: '_blank',
                  rel: 'noreferrer'
                }, 'Approve on Apify ↗')
              )
            ) : null
          )
        : h('div', null,
            h('input', {
              className: 'wf-params-input',
              type: 'text',
              value: query,
              placeholder: 'Search the Apify Store (e.g. web scraper)…',
              onChange: function (e) { setQuery(e.target.value); }
            }),
            searching ? h('div', { className: 'wf-apify-hint' }, 'Searching…') : null,
            searchErr ? h('div', { className: 'wf-apify-error' }, searchErr) : null,
            results.length
              ? h('div', { className: 'wf-apify-results' },
                  results.map(function (a) {
                    return h('button', {
                      key: a.id,
                      type: 'button',
                      className: 'wf-apify-result',
                      onClick: function () { selectActor(a); }
                    },
                      a.pictureUrl
                        ? h('img', { className: 'wf-apify-result-logo', src: a.pictureUrl, alt: '', onError: function (e) { e.target.style.display = 'none'; } })
                        : null,
                      h('div', { className: 'wf-apify-result-info' },
                        h('div', { className: 'wf-apify-result-title' }, a.title || a.name),
                        h('div', { className: 'wf-apify-result-sub' }, a.username + '/' + a.name),
                        h(ActorMeta, { rating: a.rating, reviewCount: a.reviewCount, pricing: a.pricing, freeTrial: a.freeTrial })
                      )
                    );
                  })
                )
              : (query.trim().length >= 2 && !searching && !searchErr
                  ? h('div', { className: 'wf-apify-hint' }, 'No actors found.')
                  : null)
          )
    ),

    // 3 — Input form (only once an actor is picked)
    actorId ? h(Section, { n: next(), label: 'Input' },
      loadingSchema ? h('div', { className: 'wf-apify-hint' }, 'Loading input fields…') : null,
      schemaErr ? h('div', { className: 'wf-apify-error' }, schemaErr) : null,
      !loadingSchema && !schemaErr && fields.length === 0
        ? h('div', null,
            h('div', { className: 'wf-apify-hint' },
              schema
                ? 'This actor documents no input fields — provide input as JSON below.'
                : 'No input schema was found — provide input as JSON below.'),
            h('textarea', {
              className: 'wf-params-input wf-apify-json',
              rows: 6,
              value: paramValues.input && paramValues.input !== '{}' ? String(paramValues.input) : '',
              placeholder: '{ }',
              onChange: function (e) { onParamChange('input', e.target.value || '{}'); }
            })
          )
        : fields.map(function (f) {
            return h(SchemaField, {
              key: actorId + ':' + f.key,
              fieldKey: f.key,
              prop: f.prop,
              required: f.required,
              value: input[f.key],
              variableNodes: variableNodes,
              onValue: function (v) { setInputKey(f.key, v); }
            });
          }),
      !apiKey ? h('div', { className: 'wf-apify-notice' },
        h('span', { className: 'wf-apify-notice-icon' }, '⚠'),
        h('span', null, 'Add your API key above — it is required to run the actor.')
      ) : null
    ) : null
  );
}

module.exports = ApifyEditor;
